import axios from 'axios';
import { mockDocumentNumberRules } from '../data/mockData.ts';
import type { ApprovalRecord, Document, DocumentStatus, DocumentVersion } from '../types/document.ts';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const DOCUMENT_API_URL = `${BASE_API_URL}/QmsDocument`;

export interface DocumentCreateDto {
  title: string;
  description?: string;
  category: string;
  ownerId?: number | string;
  docNum?: string;
  revision?: number;
  tags?: string[];
  file?: File;
  draftFileUrl?: string;
  status: string;
  approvalSteps?: any[];
  documentNumber?: string;
  currentVersion?: string;
  createdAt?: string;
  createdDate?: string;
  createdBy?: number;
  updatedBy?: number | string;
  updatedAt?: string;
  versionChanges?: string;
  templateId?: number | string;
  templateInstanceId?: number | string;
}

export interface DocumentResponse {
  id: string;
  docNum?: string;
  revision?: number;
  ownerId?: string;
  ownerName?: string;
  documentNumber: string;
  title: string;
  description: string;
  category: string;
  status: string;
  currentVersion: string;
  tags: string[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  draftFileUrl?: string;
  templateInstanceId?: string;
}

const mapStatusToString = (status: unknown): DocumentStatus => {
  if (status === 0 || status === '0') return 'Draft';
  if (status === 1 || status === '1') return 'Review';
  if (status === 2 || status === '2') return 'Approved';
  if (status === 3 || status === '3') return 'Obsolete';

  const normalized = String(status ?? '').toLowerCase();
  if (normalized === 'draft') return 'Draft';
  if (normalized === 'review' || normalized === 'inreview' || normalized === 'underreview') return 'Review';
  if (normalized === 'approved') return 'Approved';
  if (normalized === 'obsolete') return 'Obsolete';

  return 'Draft';
};

const mapApprovalAction = (value: unknown): ApprovalRecord['action'] => {
  if (value === 0 || value === '0') return 'Pending';
  if (value === 1 || value === '1') return 'Approved';
  if (value === 2 || value === '2') return 'Rejected';
  if (value === 3 || value === '3') return 'Deputy';

  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'deputy' || normalized === 'delegated') return 'Deputy';
  if (normalized === 'approved' || normalized === 'approve') return 'Approved';
  if (normalized === 'rejected' || normalized === 'reject') return 'Rejected';
  return 'Pending';
};

const normalizeVersions = (raw: any): DocumentVersion[] => {
  const source = raw?.versions || raw?.versionHistory || raw?.versionHistories || raw?.qmsDocumentVersions || [];
  if (!Array.isArray(source) || source.length === 0) {
    if (!raw?.draftFileUrl) return [];
    return [
      {
        version: String(raw?.currentVersion || '1.0'),
        createdAt: raw?.updatedAt || raw?.createdAt || new Date().toISOString(),
        createdBy: String(raw?.updatedBy || raw?.createdBy || ''),
        changes: '',
        status: mapStatusToString(raw?.status),
        isActive: true,
        fileUrl: raw?.draftFileUrl,
      }
    ];
  }

  return source.map((v: any, idx: number) => {
    const fileSize = Number(v?.fileSize ?? v?.size ?? 0);
    const versionValue = String(v?.version ?? v?.versionNo ?? v?.versionNumber ?? idx + 1);
    const ownerObj = v?.owner ?? v?.ownerInfo ?? null;
    const ownerName = typeof ownerObj === 'object' && ownerObj != null
      ? String(ownerObj.username ?? ownerObj.name ?? ownerObj.email ?? '')
      : v?.ownerName ?? v?.ownerUserName ?? undefined;
    return {
      id: v?.id ?? v?.versionId ?? undefined,
      version: versionValue,
      createdAt: v?.createdAt || v?.createdOn || v?.updatedAt || raw?.updatedAt || raw?.createdAt || new Date().toISOString(),
      createdBy: String(v?.createdBy ?? v?.createdByName ?? v?.userName ?? v?.updatedBy ?? raw?.createdBy ?? ''),
      changes: v?.changes || v?.changeLog || v?.description || '',
      status: mapStatusToString(v?.status ?? raw?.status),
      isActive: Boolean(v?.isActive ?? v?.active ?? (versionValue === String(raw?.currentVersion ?? ''))),
      fileUrl: v?.fileUrl || v?.url || v?.filePath || v?.downloadUrl || undefined,
      fileSize: Number.isFinite(fileSize) && fileSize > 0 ? fileSize : undefined,
      versionChanges: v?.versionChanges || v?.versionChangeNote || v?.changeNote || undefined,
      ownerName: ownerName || undefined,
      documentNumber: v?.documentNumber ?? v?.docNumber ?? undefined,
      title: v?.title ?? undefined,
      category: v?.category ?? undefined,
    } as DocumentVersion;
  });
};

const normalizeTags = (raw: any): string[] => {
  const direct = raw?.tags ?? raw?.tagList ?? raw?.tagNames ?? raw?.documentTags;
  if (Array.isArray(direct)) {
    return direct
      .map((item: any) => {
        if (typeof item === 'string') return item;
        return item?.name ?? item?.tagName ?? item?.tag ?? '';
      })
      .map((item: any) => String(item || '').trim())
      .filter(Boolean);
  }

  if (typeof direct === 'string') {
    return direct.split(',').map((tag: string) => tag.trim()).filter(Boolean);
  }

  if (Array.isArray(raw?.qmsDocumentTags)) {
    return raw.qmsDocumentTags
      .map((item: any) => String(item?.tagName ?? item?.name ?? item?.tag ?? '').trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeApprovalHistory = (raw: any): ApprovalRecord[] => {
  // Handle workflow instance structure with nested stepInstances and approvalRecords
  if (Array.isArray(raw?.stepInstances) && raw.stepInstances.length > 0) {
    const approvalRecords: ApprovalRecord[] = [];
    
    raw.stepInstances.forEach((stepInstance: any) => {
      const stepNo = stepInstance?.stepNo ?? stepInstance?.stepNumber ?? 0;
      
      if (Array.isArray(stepInstance?.approvalRecords)) {
        stepInstance.approvalRecords.forEach((record: any, recordIdx: number) => {
          approvalRecords.push({
            id: String(record?.id ?? `${raw?.id}-step${stepNo}-${recordIdx}`),
            stepInstanceId: String(record?.stepInstanceId ?? stepInstance?.id ?? stepInstance?.stepInstanceId ?? ''),
            documentId: String(record?.docId ?? raw?.docId ?? raw?.id ?? ''),
            version: String(record?.docVersion ?? raw?.docVersion ?? raw?.currentVersion ?? ''),
            approver: String(record?.approverName ?? record?.approverId ?? '-'),
            approverId: String(record?.approverId ?? record?.userId ?? ''),
            action: mapApprovalAction(record?.status ?? record?.action ?? record?.result),
            comments: String(record?.comment ?? record?.comments ?? ''),
            timestamp: String(record?.actionTime ?? record?.timestamp ?? record?.createdAt ?? new Date().toISOString()),
            level: Number(stepNo),
          });
        });
      }
    });
    
    return approvalRecords;
  }

  // Handle flat approval records structure (legacy format)
  const source =
    raw?.approvalHistory ||
    raw?.approvalHistories ||
    raw?.approvalRecords ||
    raw?.approvals ||
    raw?.workflowHistory ||
    [];

  if (!Array.isArray(source)) return [];

  return source.map((record: any, idx: number) => ({
    id: String(record?.id ?? `${raw?.id || 'doc'}-${idx}`),
    stepInstanceId: String(record?.stepInstanceId ?? record?.workflowStepInstanceId ?? record?.stepId ?? ''),
    documentId: String(record?.documentId ?? raw?.id ?? ''),
    version: String(record?.version ?? record?.versionNo ?? raw?.currentVersion ?? ''),
    approver: String(record?.approver ?? record?.approverName ?? record?.userName ?? record?.createdBy ?? '-'),
    action: mapApprovalAction(record?.action ?? record?.status ?? record?.result),
    comments: String(record?.comments ?? record?.comment ?? ''),
    timestamp: String(record?.timestamp ?? record?.createdAt ?? record?.approvedAt ?? new Date().toISOString()),
    level: Number(record?.level ?? record?.stepNo ?? record?.stepNumber ?? idx + 1),
  }));
};

const normalizeDocumentResponse = (raw: any): Document => {
  const versions = normalizeVersions(raw);
  const activeVersion = versions.find(v => v.isActive) || versions[versions.length - 1];
  const tags = normalizeTags(raw);

  return {
    id: String(raw?.id ?? ''),
    docNum: raw?.docNum != null ? String(raw.docNum) : undefined,
    revision: raw?.revision != null ? Number(raw.revision) : undefined,
    ownerId:
      raw?.ownerId != null
        ? String(raw.ownerId)
        : typeof raw?.owner === 'object' && raw?.owner != null
          ? String(raw.owner.id ?? '')
          : raw?.owner != null
            ? String(raw.owner)
            : undefined,
    ownerName:
      typeof raw?.owner === 'object' && raw?.owner != null
        ? String(raw.owner.username ?? raw.owner.name ?? raw.owner.email ?? '')
        : raw?.ownerName != null
          ? String(raw.ownerName)
          : raw?.ownerUserName != null
            ? String(raw.ownerUserName)
            : undefined,
    documentNumber: String(raw?.documentNumber ?? raw?.docNumber ?? ''),
    title: String(raw?.title ?? ''),
    description: String(raw?.description ?? raw?.documentDescription ?? raw?.desc ?? ''),
    category: String(raw?.category ?? ''),
    status: mapStatusToString(raw?.status),
    currentVersion: String(raw?.currentVersion ?? activeVersion?.version ?? '1.0'),
    versions,
    createdAt: String(raw?.createdAt ?? raw?.createdOn ?? new Date().toISOString()),
    createdBy: String(raw?.createdBy ?? raw?.createdByName ?? ''),
    updatedAt: String(raw?.updatedAt ?? raw?.createdAt ?? new Date().toISOString()),
    updatedBy: String(raw?.updatedBy ?? raw?.updatedByName ?? raw?.createdBy ?? ''),
    tags,
    approvalHistory: normalizeApprovalHistory(raw),
    draftFileUrl: raw?.draftFileUrl || activeVersion?.fileUrl,
    templateInstanceId: raw?.templateInstanceId != null ? String(raw.templateInstanceId) : undefined,
    versionChanges: raw?.versionChanges != null ? String(raw.versionChanges) : undefined,
  };
};

const DOC_NUM_REGEX = /^\d+\.\d+$/;

const parseDocNum = (docNum: string): { major: number; minor: number } | null => {
  if (!DOC_NUM_REGEX.test(docNum)) return null;
  const [majorStr, minorStr] = docNum.split('.');
  const major = Number(majorStr);
  const minor = Number(minorStr);
  if (!Number.isInteger(major) || !Number.isInteger(minor) || major < 0 || minor < 0) {
    return null;
  }
  return { major, minor };
};

const compareDocNum = (a: string, b: string): number => {
  const pa = parseDocNum(a);
  const pb = parseDocNum(b);
  if (!pa && !pb) return 0;
  if (!pa) return -1;
  if (!pb) return 1;
  if (pa.major !== pb.major) return pa.major - pb.major;
  return pa.minor - pb.minor;
};

const extractDocNum = (item: any): string | null => {
  const candidate = item?.docNum ?? item?.docnum ?? item?.doc_no ?? item?.DocNum;
  if (candidate == null) return null;
  const normalized = String(candidate).trim();
  return DOC_NUM_REGEX.test(normalized) ? normalized : null;
};

const fetchAllDocumentRawItems = async (): Promise<any[]> => {
  const token = localStorage.getItem('token');
  const response = await axios.get<any>(DOCUMENT_API_URL, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.data)) return response.data.data;
  if (Array.isArray(response.data?.items)) return response.data.items;
  return [];
};

export const getNextDocNum = async (title?: string): Promise<string> => {
  try {
    const normalizedTitle = String(title || '').trim();
    if (!normalizedTitle) {
      return '1.1';
    }
    const items = await fetchAllDocumentRawItems();
    const titleFiltered = items.filter(
      item => String(item?.title ?? '').trim().toLowerCase() === normalizedTitle.toLowerCase()
    );

    const docNums = titleFiltered
      .map(extractDocNum)
      .filter((value): value is string => Boolean(value));

    if (docNums.length === 0) return '1.1';

    const maxDocNum = docNums.reduce((max, current) => (
      compareDocNum(current, max) > 0 ? current : max
    ), docNums[0]);

    const parsed = parseDocNum(maxDocNum);
    if (!parsed) return '1.1';
    return `${parsed.major}.${parsed.minor + 1}`;
  } catch (error) {
    console.error('Failed to get next docNum, fallback to 1.1', error);
    return '1.1';
  }
};

export const validateDocNum = async (
  docNum: string,
  title: string,
  currentDocumentId?: string
): Promise<{ isValid: boolean; isDuplicate: boolean; isLowerThanExisting: boolean; message?: string }> => {
  const normalized = String(docNum || '').trim();

  if (!normalized) {
    return {
      isValid: false,
      isDuplicate: false,
      isLowerThanExisting: false,
      message: 'DocNum is required',
    };
  }

  const token = localStorage.getItem('token');
  const excludeDocId = currentDocumentId && /^\d+$/.test(String(currentDocumentId))
    ? Number(currentDocumentId)
    : undefined;

  const response = await axios.get<any>(`${DOCUMENT_API_URL}/check-docnum-duplicate`, {
    params: {
      docNum: normalized,
      excludeDocId,
    },
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  const isValid = Boolean(response?.data?.valid);
  const message = response?.data?.message ? String(response.data.message) : undefined;
  const lowerThanExisting = message?.toLowerCase().includes('lower than') || false;

  return {
    isValid,
    isDuplicate: !isValid && !lowerThanExisting,
    isLowerThanExisting: !isValid && lowerThanExisting,
    message,
  };
};

export const checkTitleUnique = async (
  title: string,
  excludeDocId?: string | number
): Promise<{ isValid: boolean; isDuplicate: boolean; message?: string }> => {
  const normalizedTitle = String(title || '').trim();
  if (!normalizedTitle) {
    return {
      isValid: false,
      isDuplicate: false,
      message: 'Title is required',
    };
  }

  const token = localStorage.getItem('token');
  const normalizedExcludeDocId = excludeDocId != null && /^\d+$/.test(String(excludeDocId))
    ? Number(excludeDocId)
    : undefined;

  const response = await axios.get<any>(`${DOCUMENT_API_URL}/check-title`, {
    params: {
      title: normalizedTitle,
      excludeDocId: normalizedExcludeDocId,
    },
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  const valid = Boolean(response?.data?.valid);
  const isDuplicate = Boolean(response?.data?.isDuplicate);

  return {
    isValid: valid,
    isDuplicate,
    message: isDuplicate ? `Document title "${normalizedTitle}" already exists` : undefined,
  };
};

export const checkDocNumUnique = async (
  docNum: string,
  excludeDocId?: string | number
): Promise<{ isValid: boolean; message?: string }> => {
  const normalizedDocNum = String(docNum || '').trim();
  if (!normalizedDocNum) {
    return { isValid: false, message: 'DocNum is required' };
  }

  const token = localStorage.getItem('token');
  const normalizedExcludeDocId = excludeDocId != null && /^\d+$/.test(String(excludeDocId))
    ? Number(excludeDocId)
    : undefined;

  const response = await axios.get<any>(`${DOCUMENT_API_URL}/check-docnum-duplicate`, {
    params: {
      docNum: normalizedDocNum,
      excludeDocId: normalizedExcludeDocId,
    },
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  return {
    isValid: Boolean(response?.data?.valid),
    message: response?.data?.message ? String(response.data.message) : undefined,
  };
};

/**
 * 通过后端接口下载文档文件
 * @param docId 文档ID
 * @param fallbackFileName 下载文件名（后端未返回时使用）
 * @param useDraft 是否下载草稿文件，默认true
 */
export const downloadDocumentFile = async (
  docId: string | number,
  fallbackFileName = 'document',
  useDraft = true
): Promise<void> => {
  if (!docId) throw new Error('文档ID无效');
  const token = localStorage.getItem('token');
  const downloadApi = `${BASE_API_URL}/QmsDocument/documents/${docId}/download`;
  try {
    const response = await axios.get(downloadApi, {
      params: { useDraft },
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    let fileName = fallbackFileName;
    if (contentDisposition) {
      const fileNameFromHeader = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i)?.[1];
      const decodedHeaderName = fileNameFromHeader ? decodeURIComponent(fileNameFromHeader.replace(/"/g, '')) : '';
      if (decodedHeaderName) fileName = decodedHeaderName;
    }
    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    console.error('文件下载失败:', error);
    throw new Error(error?.response?.data?.message || error?.response?.data?.detail || '文件下载失败');
  }
};

/**
 * Generate document number in format: QMS-{TypeAbbr}-{Year}-{Version}
 * Example: QMS-SOP-2026-001
 * @param category - Document category (e.g., 'Standard Operating Procedure', 'Work Instruction', 'SOP')
 * @param version - Document version (e.g., '1.0', '2.5'), will be converted to 3-digit format (001, 002, etc.)
 * @returns Generated document number
 */
export const generateDocumentNumber = (category: string, version?: string): string => {
  const currentYear = new Date().getFullYear();
  
  // Find the corresponding rule in mockDocumentNumberRules
  const rule = mockDocumentNumberRules.find(
    r => r.category === category || r.category.toLowerCase() === category.toLowerCase()
  );
  
  // Extract type abbreviation from prefix (e.g., 'QMS-SOP' -> 'SOP')
  let typeAbbr = 'DOC';
  if (rule && rule.prefix) {
    const parts = rule.prefix.split('-');
    if (parts.length > 1) {
      typeAbbr = parts[parts.length - 1]; // Get the last part after QMS-
    }
  } else {
    // If no rule found, generate abbreviation from category
    // Take first letter of each word (e.g., 'Standard Operating Procedure' -> 'SOP')
    const words = category.split(' ');
    typeAbbr = words.map(word => word.charAt(0).toUpperCase()).join('');
  }
  
  // Convert version to 3-digit sequence number
  let sequenceNum = '001';
  if (version) {
    // If version is like '1.0', extract the major version
    const majorVersion = parseInt(version.split('.')[0]);
    if (!isNaN(majorVersion) && majorVersion > 0) {
      sequenceNum = majorVersion.toString().padStart(3, '0');
    }
  }
  
  return `QMS-${typeAbbr}-${currentYear}-${sequenceNum}`;
};

/**
 * Create a new document
 * Calls the POST /api/QmsDocument endpoint with FormData
 */
export const createDocument = async (data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    
    // Create FormData to send file and other data
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('docNum', data.docNum || '1.0');
    formData.append('revision', String(data.revision ?? 1));
    formData.append('status', data.status);
    
    // Required fields: use the document number provided by the page only.
    formData.append('documentNumber', String(data.documentNumber ?? data.docNum ?? ''));
    formData.append('currentVersion', data.currentVersion || '1.0');
    
    // Get current user ID from localStorage or use default
    const currentUser = localStorage.getItem('userId');
    formData.append('createdBy', data.createdBy?.toString() || currentUser || '1000');

    if (data.ownerId !== undefined && data.ownerId !== null && String(data.ownerId).trim() !== '') {
      const normalizedOwner = String(data.ownerId).trim();
      formData.append('ownerId', normalizedOwner);
      formData.append('owner', normalizedOwner);
    }
    
    // Add templateInstanceId if provided
    if (data.templateId !== undefined && data.templateId !== null && String(data.templateId).trim() !== '') {
      formData.append('templateId', String(data.templateId));
    }

    // Add templateInstanceId if provided
    if (data.templateInstanceId) {
      formData.append('templateInstanceId', data.templateInstanceId.toString());
    }
    
    formData.append('description', String(data.description ?? ''));
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      // If no new file is uploaded but draftFileUrl is provided, use it
      formData.append('draftFileUrl', data.draftFileUrl);
    }
    
    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    if (data.versionChanges) {
      formData.append('versionChanges', data.versionChanges);
    }

    const response = await axios.post<DocumentResponse>(DOCUMENT_API_URL, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to create document:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to create document'
    );
  }
};

/**
 * Create a document revision
 * Calls the POST /api/QmsDocument/revision endpoint with FormData
 */
export const createDocumentRevision = async (originalDocId: string, data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('id', originalDocId);
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('docNum', data.docNum || '1.0');
    formData.append('revision', String(data.revision ?? 1));
    formData.append('status', data.status);
    formData.append('documentNumber', String(data.documentNumber ?? data.docNum ?? ''));
    formData.append('currentVersion', data.currentVersion || '1.0');
    const currentUser = localStorage.getItem('userId');
    formData.append('createdBy', data.createdBy?.toString() || currentUser || '1000');

    if (data.ownerId !== undefined && data.ownerId !== null && String(data.ownerId).trim() !== '') {
      const normalizedOwner = String(data.ownerId).trim();
      formData.append('ownerId', normalizedOwner);
      formData.append('owner', normalizedOwner);
    }

    if (data.templateId !== undefined && data.templateId !== null && String(data.templateId).trim() !== '') {
      formData.append('templateId', String(data.templateId));
    }

    if (data.templateInstanceId) {
      formData.append('templateInstanceId', data.templateInstanceId.toString());
    }

    formData.append('description', String(data.description ?? ''));

    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }

    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      formData.append('draftFileUrl', data.draftFileUrl);
    }

    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    if (data.versionChanges) {
      formData.append('versionChanges', data.versionChanges);
    }

    const response = await axios.post<DocumentResponse>(`${DOCUMENT_API_URL}/revision`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to create document revision:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to create document revision'
    );
  }
};

/**
 * Update an existing document
 * Calls the PUT /api/QmsDocument/{id} endpoint with FormData
 */
export const updateDocument = async (id: string, data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    // Add the ID to match the URL parameter
    formData.append('id', id);
    formData.append('title', data.title);
    formData.append('category', data.category);
    if (data.docNum) {
      formData.append('docNum', data.docNum);
    }
    formData.append('revision', String(data.revision ?? 1));
    formData.append('status', data.status);
    
    // Required fields
    formData.append('documentNumber', String(data.documentNumber ?? data.docNum ?? ''));
    if (data.currentVersion) {
      formData.append('currentVersion', data.currentVersion);
    }
    if (data.createdAt) {
      formData.append('createdAt', data.createdAt);
    }
    if (data.createdDate) {
      formData.append('createdDate', data.createdDate);
    }
    
    // Get current user ID from localStorage or use default
    const currentUser = localStorage.getItem('userId');
    if (data.createdBy) {
      formData.append('createdBy', data.createdBy.toString());
    } else if (currentUser) {
      formData.append('createdBy', currentUser);
    }

    if (data.ownerId !== undefined && data.ownerId !== null && String(data.ownerId).trim() !== '') {
      const normalizedOwner = String(data.ownerId).trim();
      formData.append('ownerId', normalizedOwner);
      formData.append('owner', normalizedOwner);
    }
    
    // Add updatedBy and updatedAt for document updates
    if (data.updatedBy) {
      formData.append('updatedBy', data.updatedBy.toString());
    } else if (currentUser) {
      formData.append('updatedBy', currentUser);
    }
    if (data.updatedAt) {
      formData.append('updatedAt', data.updatedAt);
    } else {
      // Use current timestamp if not provided
      formData.append('updatedAt', new Date().toISOString());
    }
    
    // Add templateInstanceId if provided
    if (data.templateInstanceId) {
      formData.append('templateInstanceId', data.templateInstanceId.toString());
    }
    
    formData.append('description', String(data.description ?? ''));
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      // If no new file is uploaded, preserve existing draftFileUrl
      formData.append('draftFileUrl', data.draftFileUrl);
    }
    
    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    if (data.versionChanges) {
      formData.append('versionChanges', data.versionChanges);
    }

    const response = await axios.put<DocumentResponse>(`${DOCUMENT_API_URL}/${id}`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to update document:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to update document'
    );
  }
};

/**
 * Overwrite document file only — does not touch status or workflow.
 * Calls PUT /api/QmsDocument/{id} with only file/versionChanges/updatedBy.
 */
export const overwriteDocumentFile = async (
  id: string,
  data: Omit<DocumentCreateDto, 'approvalSteps'>
): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    const currentUser = localStorage.getItem('userId');

    const formData = new FormData();
    formData.append('id', id);
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('description', String(data.description ?? ''));
    formData.append('status', data.status);
    if (data.docNum) formData.append('docNum', data.docNum);
    if (data.documentNumber) formData.append('documentNumber', data.documentNumber);
    if (data.currentVersion) formData.append('currentVersion', data.currentVersion);
    if (data.createdAt) formData.append('createdAt', data.createdAt);
    if (data.createdDate) formData.append('createdDate', data.createdDate);
    if (data.createdBy) formData.append('createdBy', String(data.createdBy));
    if (data.ownerId !== undefined && data.ownerId !== null && String(data.ownerId).trim() !== '') {
      formData.append('ownerId', String(data.ownerId));
      formData.append('owner', String(data.ownerId));
    }
    formData.append('updatedBy', String(data.updatedBy ?? currentUser ?? ''));
    formData.append('updatedAt', data.updatedAt ?? new Date().toISOString());
    if (data.draftFileUrl) formData.append('draftFileUrl', data.draftFileUrl);
    if (data.versionChanges) formData.append('versionChanges', data.versionChanges);
    if (data.templateInstanceId) formData.append('templateInstanceId', String(data.templateInstanceId));
    if (data.revision !== undefined) formData.append('revision', String(data.revision));
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => formData.append(`tags[${index}]`, tag));
    }

    const response = await axios.put<DocumentResponse>(`${DOCUMENT_API_URL}/${id}/rewrite`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to rewrite document:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to rewrite document'
    );
  }
};

/**
 * Get document by ID
 */
export const getDocumentById = async (id: string): Promise<Document> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<any>(`${DOCUMENT_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    const payload = response.data?.document ?? response.data?.data ?? response.data;
    return normalizeDocumentResponse(payload);
  } catch (error: any) {
    console.error(`Failed to get document ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get document'
    );
  }
};

/**
 * Delete document by ID
 */
export const deleteDocument = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${DOCUMENT_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to delete document ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to delete document'
    );
  }
};

/**
 * Submit document for approval
 * Calls the POST /api/QmsDocument/documents/{docId}/submit endpoint
 */
export const submitDocumentForApproval = async (
  docId: string | number,
  templateInstanceId?: number,
  approvalSteps?: any[]
): Promise<{ docId: number; id: number; currentStepNo: number }> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    // Add approvalStepsJson if provided
    if (approvalSteps && approvalSteps.length > 0) {
      formData.append('approvalStepsJson', JSON.stringify(approvalSteps));
    }

    const params: any = {};
    if (templateInstanceId) {
      params.templateInstanceId = templateInstanceId;
    }

    const response = await axios.post(
      `${DOCUMENT_API_URL}/documents/${docId}/submit`,
      formData,
      {
        params,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error(`Failed to submit document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to submit document for approval'
    );
  }
};

/**
 * Commit/approve a document
 * Calls the POST /api/QmsDocument/documents/{docId}/commit endpoint
 */
export const commitDocument = async (
  docId: string | number,
  action: 'Approve' | 'Reject' = 'Approve',
  comments?: string
): Promise<{ id: number; status: string; currentStepNo: number }> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    formData.append('action', action);
    if (comments) {
      formData.append('comments', comments);
    }

    const response = await axios.post(
      `${DOCUMENT_API_URL}/documents/${docId}/commit`,
      formData,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error(`Failed to commit document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to commit document'
    );
  }
};

/**
 * Replace current approver with deputy user
 * POST /api/QmsMyTask/deputy
 * Request body: { stepInstanceId, docId, fromUserId, userId }
 */
export const replaceApproverWithDeputy = async (
  stepInstanceId: string | number,
  docId: string | number,
  fromUserId: string | number,
  userId: string | number,
  comment?: string
): Promise<any> => {
  const token = localStorage.getItem('token');
  const normalizedComment = String(comment || '').trim();
  const payload = {
    stepInstanceId: Number(stepInstanceId),
    docId: Number(docId),
    fromUserId: Number(fromUserId),
    userId: Number(userId),
    ...(normalizedComment ? { Comment: normalizedComment } : {}),
  };

  try {
    const response = await axios.post(
      `${BASE_API_URL}/QmsMyTask/deputy`,
      payload,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Failed to assign deputy approver:', error);
    const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to assign deputy approver';
    throw new Error(errorMsg);
  }
};

const unwrapListPayload = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

export const getDocumentApprovalInfo = async (docId: string): Promise<ApprovalRecord[]> => {
  try {
    const token = localStorage.getItem('token');
    const headers = { Authorization: token ? `Bearer ${token}` : '' };
    
    // Call the endpoint: /api/QmsDocument/{id}/approval-info
    // This is the official endpoint for getting document approval information
    const response = await axios.get(`${DOCUMENT_API_URL}/${docId}/approval-info`, { headers });
    const data = response.data;
    
    // The response could be:
    // 1. A workflow instance object with stepInstances (new structure)
    // 2. A wrapped response with data property containing approval records
    // 3. A direct array of approval records
    // Pass the data directly to normalizeApprovalHistory which handles all formats
    
    return normalizeApprovalHistory(data);
  } catch (error: any) {
    console.error(`Failed to get approval info for document ${docId}:`, error);
    const status = error?.response?.status;
    if (status === 404 || status === 204) {
      return []; // Document not found or no approval info
    }
    // Log the error but don't fallback - fail explicitly if the official endpoint fails
    throw new Error(
      error?.response?.data?.message || 
      error?.response?.data?.detail || 
      `Failed to get approval info for document ${docId}`
    );
  }
};

export const getApprovalRecordsByDocumentId = async (docId: string): Promise<ApprovalRecord[]> => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: token ? `Bearer ${token}` : '' };

  const endpointAttempts: Array<() => Promise<any>> = [
    () => axios.get(`${BASE_API_URL}/QmsApprovalRecords/byDocument/${docId}`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsApprovalRecords/document/${docId}`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsApprovalRecords`, {
      headers,
      params: {
        range: '[0,999]',
        perPage: 1000,
        filter: JSON.stringify({ documentId: Number.isNaN(Number(docId)) ? docId : Number(docId) })
      }
    })
  ];

  for (const request of endpointAttempts) {
    try {
      const response = await request();
      const list = unwrapListPayload(response.data);
      return normalizeApprovalHistory({ id: docId, approvalRecords: list });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status && status !== 400 && status !== 404) {
        throw new Error(error?.response?.data?.message || error?.response?.data?.detail || 'Failed to get approval records');
      }
    }
  }

  return [];
};

export const getDocumentVersionsByDocumentId = async (docId: string): Promise<DocumentVersion[]> => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: token ? `Bearer ${token}` : '' };

  const endpointAttempts: Array<() => Promise<any>> = [
    () => axios.get(`${BASE_API_URL}/QmsDocumentVersion/byDocument/${docId}`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsDocumentVersions/byDocument/${docId}`, { headers }),
    () => axios.get(`${DOCUMENT_API_URL}/${docId}/versions`, { headers }),
    () => axios.get(`${BASE_API_URL}/QmsDocumentVersion`, {
      headers,
      params: {
        range: '[0,999]',
        perPage: 1000,
        filter: JSON.stringify({ documentId: Number.isNaN(Number(docId)) ? docId : Number(docId) })
      }
    })
  ];

  for (const request of endpointAttempts) {
    try {
      const response = await request();
      const list = unwrapListPayload(response.data);
      return normalizeVersions({ id: docId, versions: list });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status && status !== 400 && status !== 404) {
        throw new Error(error?.response?.data?.message || error?.response?.data?.detail || 'Failed to get document versions');
      }
    }
  }

  return [];
};

export const getDocumentVersionById = async (versionId: string): Promise<DocumentVersion | null> => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: token ? `Bearer ${token}` : '' };
  try {
    const response = await axios.get(`${BASE_API_URL}/QmsDocumentVersion/${versionId}`, { headers });
    const raw = response.data?.data ?? response.data;
    const versions = normalizeVersions({ versions: [raw] });
    return versions[0] ?? null;
  } catch {
    return null;
  }
};

/**
 * Download a document file
 * Calls the GET /api/QmsDocument/documents/{docId}/download endpoint
 */
export const downloadDocument = async (docId: string | number, version?: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(
      `${DOCUMENT_API_URL}/documents/${docId}/download`,
      {
        params: version ? { version } : {},
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        responseType: 'blob',
      }
    );

    // Create a temporary URL for the blob and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `document-${docId}`;
    
    if (contentDisposition && contentDisposition.indexOf('filename=') !== -1) {
      filename = contentDisposition
        .split('filename=')[1]
        .split(';')[0]
        .replace(/"/g, '')
        .replace(/'/g, '');
    }
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error(`Failed to download document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to download document'
    );
  }
};

/**
 * Get workflow instance for a document
 * Calls the GET /api/QmsDocument/{id}/workflow-instance endpoint
 */
export const getDocumentWorkflowInstance = async (docId: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const headers = { Authorization: token ? `Bearer ${token}` : '' };

    const response = await axios.get(`${DOCUMENT_API_URL}/${docId}/workflow-instance`, { headers });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to get workflow instance for document ${docId}:`, error);
    const status = error?.response?.status;
    if (status === 404 || status === 204) {
      return null; // No workflow instance found
    }
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to get workflow instance'
    );
  }
};

/**
 * Mark document review as no change (only update review date)
 * Calls the POST /api/QmsDocument/{id}/review/no-change endpoint
 */
export const markDocumentReviewNoChange = async (id: string, updatedBy?: string): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    if (updatedBy) {
      formData.append('updatedBy', updatedBy);
    }

    const response = await axios.post<DocumentResponse>(`${DOCUMENT_API_URL}/${id}/review/no-change`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to mark document review no change:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to mark document review no change'
    );
  }
};

/**
 * Start document review revision
 * Calls the POST /api/QmsDocument/{id}/review/revision endpoint
 */
export const startDocumentReviewRevision = async (id: string, file: File, updatedBy?: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    if (updatedBy) {
      formData.append('updatedBy', updatedBy);
    }

    const response = await axios.post(`${DOCUMENT_API_URL}/${id}/review/revision`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to start document review revision:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to start document review revision'
    );
  }
};

/**
 * Start document review revision
 * Calls the POST /api/QmsDocument/{id}/review/revision endpoint
 */
export const startDocumentReviewRevisionWithVersion = async (
  id: string,
  file: File,
  updatedBy?: string,
  revisionVersion?: string
): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    if (updatedBy) {
      formData.append('updatedBy', updatedBy);
    }
    if (revisionVersion && String(revisionVersion).trim() !== '') {
      formData.append('revisionVersion', String(revisionVersion).trim());
    }

    const response = await axios.post(`${DOCUMENT_API_URL}/${id}/review/revision`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to start document review revision:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to start document review revision'
    );
  }
};

/**
 * Recall document from review and reset workflow to draft
 * Calls the POST /api/QmsDocument/{id}/recall endpoint
 */
export const recallDocumentToDraft = async (id: string, updatedBy?: string): Promise<Document> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    if (updatedBy) {
      formData.append('updatedBy', updatedBy);
    }

    const response = await axios.post(`${DOCUMENT_API_URL}/${id}/recall`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });

    const payload = response.data?.document ?? response.data?.data ?? response.data;
    return normalizeDocumentResponse(payload);
  } catch (error: any) {
    console.error('Failed to recall document to draft:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to recall document to draft'
    );
  }
};
